import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * ADMIN Sale Dashboard — sale-level counts. AdminRoute-gated (no consignor).
 *
 * Every number here is derived from data that already exists. Views/watches are NOT rendered
 * at all: nothing in the schema tracks them, and a zero column would be an invented stat.
 */

const money = (cents?: number | null) => `$${(((cents ?? 0)) / 100).toFixed(2)}`;

const Stat: React.FC<{ label: string; value: React.ReactNode; note?: string }> = ({ label, value, note }) => (
  <div className="card">
    <p className="text-sm text-gray-600">{label}</p>
    <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
    {note && <p className="text-xs text-gray-500 mt-1">{note}</p>}
  </div>
);

const SaleDashboard: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [stats, setStats] = useState({
    registrations: 0,
    ccAuths: 0,
    regsWithoutAuthField: 0,
    uniqueBidders: 0,
    totalBids: 0,
    voidedBids: 0,
    lotCount: 0,
    lotsWithBids: 0,
    highestBid: 0,
    totalCurrentValue: 0,
  });

  useEffect(() => {
    let active = true;
    (async () => {
      if (!auctionId) return;
      try {
        const [aSnap, regSnap, bidSnap] = await Promise.all([
          getDoc(doc(db, 'auctions', auctionId)),
          getDocs(query(collection(db, 'saleRegistrations'), where('auctionId', '==', auctionId))),
          getDocs(query(collection(db, 'bids'), where('auctionId', '==', auctionId))),
        ]);
        if (!active) return;

        const a: any = aSnap.exists() ? aSnap.data() : {};
        const lots: any[] = a.lots || [];

        let ccAuths = 0;
        let regsWithoutAuthField = 0;
        regSnap.forEach((d) => {
          const r: any = d.data();
          if (r.cardAuthRan === true) ccAuths++;
          else if (r.cardAuthRan === undefined) regsWithoutAuthField++;
        });

        const bidders = new Set<string>();
        let totalBids = 0;
        let voidedBids = 0;
        bidSnap.forEach((d) => {
          const b: any = d.data();
          const voided = b.status === 'rejected' || !!b.voidReason;
          if (voided) { voidedBids++; return; }
          if (b.status === 'pending') return; // in flight — not yet a bid
          totalBids++;
          if (b.bidderId) bidders.add(b.bidderId);
        });

        const withBids = lots.filter((l) => (l.bidCount ?? 0) > 0);
        const highestBid = lots.reduce((m, l) => Math.max(m, l.currentBid ?? 0), 0);
        const totalCurrentValue = withBids.reduce((s, l) => s + (l.currentBid ?? 0), 0);

        setTitle(a.title || auctionId);
        setStats({
          registrations: regSnap.size,
          ccAuths,
          regsWithoutAuthField,
          uniqueBidders: bidders.size,
          totalBids,
          voidedBids,
          lotCount: lots.length,
          lotsWithBids: withBids.length,
          highestBid,
          totalCurrentValue,
        });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [auctionId]);

  if (loading) return <div className="text-center py-12 text-gray-600">Loading dashboard…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Sale Dashboard</h1>
          <p className="text-gray-600">{title}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/sale/${auctionId}/registrations`} className="btn-secondary">Registrations</Link>
          <Link to={`/admin/sale/${auctionId}/lot-stats`} className="btn-secondary">Lot Stats</Link>
          <Link to={`/sale/${auctionId}`} className="btn-secondary">View Sale</Link>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Registrations" value={stats.registrations} />
        <Stat
          label="CC Authorizations"
          value={stats.ccAuths}
          note={stats.regsWithoutAuthField > 0
            ? `${stats.regsWithoutAuthField} registration(s) predate auth tracking`
            : undefined}
        />
        <Stat label="Unique Bidders" value={stats.uniqueBidders} />
        <Stat
          label="Total Bids"
          value={stats.totalBids}
          note={stats.voidedBids > 0 ? `${stats.voidedBids} voided (excluded)` : undefined}
        />
        <Stat label="Lots" value={stats.lotCount} />
        <Stat label="Lots With Bids" value={stats.lotsWithBids} />
        <Stat label="Highest Current Bid" value={money(stats.highestBid)} />
        <Stat label="Total Current Value" value={money(stats.totalCurrentValue)} note="Sum of current bids on lots with bids" />
      </div>

      <p className="text-xs text-gray-500">
        Views and watches are not shown: nothing in the schema tracks them yet.
      </p>
    </div>
  );
};

export default SaleDashboard;
